// The only populated dictionary today. Every user-facing string in the public site routes
// through this shape via `t()` -- see LocaleProvider.tsx. Business data (service names/
// descriptions, FAQ entries, blog posts, coach bios) lives in Firestore and is a separate,
// larger problem (per-locale fields on each document) deliberately out of scope here: this
// covers UI chrome, not admin-authored content.
const en = {
  header: {
    brand: "H&Y",
    sessions: "Sessions",
    about: "About",
    gallery: "Gallery",
    faq: "FAQ",
    updates: "Updates",
    contact: "Contact",
    batches: "Group Classes",
    bookSession: "Book a session",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },
  footer: {
    rights: "Hoop & Yoga Fitness",
    privacy: "Privacy",
    terms: "Terms",
    refunds: "Refunds & Cancellations",
    tagline: "Personal hoop dance and yoga coaching with Devina Prasad, in Bangalore and online.",
    exploreHeading: "Explore",
    legalHeading: "Legal",
  },
  home: {
    hero: {
      eyebrow: "Bangalore · Online & In-person",
      title: "Move like you",
      titleAccent: "mean it",
      subtitlePrefix: "Personal hoop dance and yoga coaching with",
      subtitleName: "Devina Prasad",
      subtitleSuffix: "Real sessions, real progress, no studio required.",
      cta: "Book a session",
      secondaryCta: "Explore all services",
    },
    services: {
      eyebrow: "Sessions",
      title: "Pick a session.",
      subtitle:
        "Every session is one-on-one with Devina and built around you. Book up to 45 days ahead.",
      withCoach: "with {{coach}} · {{duration}}",
      bookThis: "Book now →",
      depositBadge: "Book from ₹{{amount}}*",
      depositFootnote: "*Deposit to hold your slot. Balance due before your session.",
      bundleNote: "Booking 8 or more sessions? Ask about bundle pricing when you book.",
      bundleBadge: "Save {{percent}}% booking 8+ sessions",
      seeAll: "See all sessions",
      items: {
        hooping: {
          name: "Hoop Choreography",
          description:
            "One-on-one hoop dance coaching, from your first spin to full choreography, built around your body and your music.",
        },
        yoga: {
          name: "Yoga",
          description:
            "Personal yoga sessions built around strength, breath, and consistency, for every level.",
        },
        "pregnancy-yoga": {
          name: "Pregnancy Yoga",
          description:
            "Gentle, trimester-aware yoga for expecting mothers, focused on comfort and preparation.",
        },
        trial: {
          name: "Trial Session",
          description: "New here? Start with a single trial session before committing to a full plan.",
        },
        "hula-hoop": {
          name: "Hula Hoop Fundamentals",
          description:
            "One-on-one hoop fitness coaching: waist hooping, tricks, and technique, no dance choreography required.",
        },
        "nutrition-plan": {
          name: "Nutrition Consultation & Plan",
          description:
            "A one-on-one consultation with a personalized nutrition plan to go alongside your sessions.",
        },
      },
    },
    about: {
      eyebrow: "Know your coach",
      title: "Devina Prasad",
      body: "Hoop dance artist, choreographer, and yoga coach. Devina built her own practice from the ground up: hoop flow and choreography for the artistry of it, yoga and pregnancy yoga for the discipline. Every session is built around where you actually are, not a fixed program.",
      cta: "Book a session now",
      readMore: "Read Devina's full story →",
    },
    gallery: {
      eyebrow: "Gallery",
      title: "See it in motion.",
    },
    groupClasses: {
      eyebrow: "Group Classes",
      title: "Train",
      titleAccent: "together",
      subtitle:
        "Recurring batches, taught live online, with recordings included if you miss a class.",
      point1: "Mon/Wed/Fri, two skill tiers to choose from",
      point2: "Every class recorded, so a missed session is never lost",
      point3: "Small enough for Devina to still correct your form",
      cta: "See group classes",
    },
    testimonials: {
      eyebrow: "What clients say",
      title: "Real progress, real people.",
      items: {
        priya: {
          quote: "I couldn't hold a hoop on my waist for more than two spins when I started. Six weeks in, Devina had me doing isolations I didn't think were possible for a beginner.",
          name: "Priya S.",
          detail: "Hoop Choreography",
        },
        ananya: {
          quote: "Devina adjusted every session around how my body actually felt that day, not a fixed plan. That's the difference between a class and actual coaching.",
          name: "Ananya R.",
          detail: "Yoga",
        },
        meera: {
          quote: "I was nervous about yoga through my third trimester. She knew exactly what to modify and never once made me feel limited.",
          name: "Meera K.",
          detail: "Pregnancy Yoga",
        },
        rohit: {
          quote: "Six months ago I couldn't get the hoop past my hips. Now I'm working on chest rolls and my niece thinks I'm basically a circus performer.",
          name: "Rohit M.",
          detail: "Hoop Choreography",
        },
        kavya: {
          quote: "I'd tried yoga apps for a year with zero consistency. Having an actual person notice when my alignment was off changed everything in a month.",
          name: "Kavya N.",
          detail: "Yoga",
        },
        sneha: {
          quote: "My doctor was cautious about me exercising at all. Devina worked directly within what he'd cleared, and I felt stronger through my second trimester, not weaker.",
          name: "Sneha T.",
          detail: "Pregnancy Yoga",
        },
        aditi: {
          quote: "I signed up expecting a workout and got an actual choreography routine I can perform. Didn't think that was realistic starting from zero.",
          name: "Aditi V.",
          detail: "Hoop Choreography",
        },
        neha: {
          quote: "Every other class I've done moves at one fixed pace for everyone. Devina slowed down exactly where I needed it without ever making it feel remedial.",
          name: "Neha J.",
          detail: "Yoga",
        },
        pooja: {
          quote: "I was mostly looking for something gentle post-delivery. She rebuilt my routine around recovery week by week instead of handing me a generic postnatal plan.",
          name: "Pooja R.",
          detail: "Pregnancy Yoga",
        },
      },
    },
    faq: {
      eyebrow: "Frequently Asked Questions",
      title: "Everything you need to know.",
      loading: "Loading...",
    },
    contact: {
      title: "Corporate event or brand collaboration?",
      body: "For workshops, corporate sessions, or brand collaborations, reach out directly and we'll get back to you.",
      cta: "Get in touch",
    },
    whatsappCommunity: {
      title: "Join our WhatsApp community.",
      body: "Session reminders, class openings, and updates from Devina, straight on WhatsApp.",
      cta: "Join us on WhatsApp",
    },
    instagram: {
      eyebrow: "Instagram",
      title: "Don't miss an update.",
      cta: "Follow us on Instagram",
    },
  },
  servicesPage: {
    eyebrow: "Sessions",
    title: "All sessions.",
    subtitle:
      "Every session is one-on-one with Devina and built around you. Book up to 45 days ahead.",
  },
  contactPage: {
    eyebrow: "Contact",
    title: "Get in touch.",
    subtitle: "Questions about a session, a class, or anything else? Send a message directly.",
    form: {
      namePlaceholder: "Full name",
      emailPlaceholder: "Email",
      messagePlaceholder: "What can we help with?",
      submitIdle: "Send message",
      submitLoading: "Sending...",
      success: "Thanks! We'll get back to you soon.",
      error: "Could not send your message. Please try again.",
    },
  },
  book: {
    back: "Back",
    service: {
      title: "Choose a session",
      empty:
        "No services configured yet. Run the seed script (see functions/README.md) against the emulator or a real Firestore project.",
      duration: "{{minutes}} min",
    },
    coach: {
      title: "Choose your coach",
    },
    slot: {
      title: "Pick a time",
      checkAvailability: "Check availability",
      checking: "Checking availability...",
      noSlots: "No free slots this day yet. Pick a date and check availability.",
      continue: "Continue",
    },
    details: {
      title: "Your details",
      namePlaceholder: "Full name",
      emailPlaceholder: "Email (optional)",
      phonePlaceholder: "WhatsApp number",
      couponPlaceholder: "Coupon code (optional)",
      holdNotice: "We'll hold this slot for 10 minutes while we confirm your booking.",
      termsPrefix: "I agree to the",
      termsLink: "Terms",
      and: "and",
      privacyLink: "Privacy Policy",
      marketingOptIn: "Send me session reminders and offers on WhatsApp (optional).",
      submitIdle: "Book my session",
      submitLoading: "Booking your session...",
      loadError: "Could not load availability. Please try again.",
      holdError: "Could not hold this slot. Please try another.",
    },
    payment: {
      title: "Almost there",
      noticeBefore: "Your slot is held under booking",
      noticeAfter: ". Pay the deposit now to confirm it.",
      payIdle: "Pay deposit",
      payLoading: "Starting payment...",
      payError: "Could not start payment. Please try again.",
    },
    confirmed: {
      title: "You're booked!",
      noticeBefore: "Booking",
      noticeAfter: "is confirmed. We'll message you on WhatsApp shortly to arrange payment.",
    },
  },
  batches: {
    list: {
      title: "Group Classes",
      subtitle: "Recurring batches, taught live online, with recordings included.",
      empty: "No batches open for enrollment right now. Check back soon.",
      seatsLeft: "{{seats}} seats left",
      full: "Full",
      starts: "Starts {{date}}",
      duration: "Runs for {{weeks}} weeks",
      enrollThis: "Enroll in this batch →",
    },
    details: {
      title: "Your details",
      namePlaceholder: "Full name",
      emailPlaceholder: "Email (optional)",
      phonePlaceholder: "WhatsApp number",
      couponPlaceholder: "Coupon code (optional)",
      holdNotice: "We'll hold your seat for 10 minutes while we confirm your enrollment.",
      termsPrefix: "I agree to the",
      termsLink: "Terms",
      and: "and",
      privacyLink: "Privacy Policy",
      marketingOptIn: "Send me class reminders and offers on WhatsApp (optional).",
      submitIdle: "Reserve my seat",
      submitLoading: "Reserving your seat...",
      enrollError: "Could not reserve a seat. Please try again.",
    },
    payment: {
      title: "Almost there",
      noticeBefore: "Your seat is held under booking",
      noticeAfter: ". Pay now to confirm it.",
      payIdle: "Pay now",
      payLoading: "Starting payment...",
      payError: "Could not start payment. Please try again.",
    },
    confirmed: {
      title: "You're enrolled!",
      noticeBefore: "Booking",
      noticeAfter: "is confirmed. We'll message you on WhatsApp with the class link before the batch starts.",
    },
  },
  confirmation: {
    missingReference: "Missing booking reference.",
    checking: "Checking your payment...",
    confirmedTitle: "You're booked!",
    confirmedBefore: "Booking",
    confirmedAfter: "is confirmed. We'll send a reminder on WhatsApp before your session.",
    heldTitle: "Almost there",
    heldBefore: "We're still confirming your payment for booking",
    heldAfter: "This can take a minute. We'll message you on WhatsApp once it's done.",
    failedTitle: "This booking didn't go through",
    failedBefore: "Booking",
    failedAfter: "is {{status}}. Please",
    pickAgain: "pick a slot again",
    statusError: "Could not check booking status.",
  },
  demoPayment: {
    badge: "Demo mode",
    title: "Simulate payment",
    notice: "No real payment gateway is connected yet. Booking {{reference}} is awaiting ₹{{amount}}. Click below to simulate a successful payment and see exactly what happens once a real gateway is live.",
    simulateIdle: "Simulate successful payment",
    simulateLoading: "Simulating...",
    simulateError: "Could not simulate this payment. Please try again.",
  },
  account: {
    auth: {
      titleSignin: "My account",
      subtitle: "Use your phone to see, reschedule, or cancel your bookings.",
    },
    dashboard: {
      title: "My bookings",
      signOut: "Sign out",
      loading: "Loading your bookings...",
      empty: "No bookings yet.",
      status: "Status: {{status}}",
      reschedule: "Reschedule",
      rescheduleLimitReached: "Reschedule limit reached",
      cancel: "Cancel",
      cancelConfirm: "Cancel this booking? Your cancellation policy applies to any refund.",
      loadError: "Could not load your bookings.",
      cancelError: "Could not cancel this booking.",
      meetingLink: "Join class →",
      meetingLinkPending: "Class link will appear here closer to the start date.",
      addEmailPrompt: "Add and verify an email for account recovery and receipts (optional).",
      skipEmail: "Skip for now",
    },
    reschedule: {
      checkAvailability: "Check availability",
      confirmIdle: "Confirm new time",
      confirmLoading: "Rescheduling...",
      loadError: "Could not load availability.",
      confirmError: "Could not reschedule this booking.",
    },
  },
  blog: {
    title: "Blog",
    loading: "Loading posts...",
    empty: "Nothing here yet.",
    allPosts: "All posts",
    backToAllPosts: "Back to all posts",
    notFound: "That post isn't here.",
  },
  legal: {
    lastUpdated: "Last updated {{date}}",
  },
  cookieConsent: {
    notice: "We use cookies for analytics, so we can see how people use this site. See our",
    privacyPolicy: "Privacy Policy",
    decline: "Decline",
    accept: "Accept",
  },
  errors: {
    notFoundTitle: "Page not found",
    notFoundBody: "That page doesn't exist, or has moved.",
    backHome: "Back home",
    serverErrorTitle: "Something went wrong",
    serverErrorBody: "Sorry about that. Try again, or head back home.",
    tryAgain: "Try again",
  },
  languageSwitcher: {
    label: "Language",
  },
  phoneVerification: {
    phoneLabel: "WhatsApp number",
    consent: "I agree to be contacted on this WhatsApp number for booking updates.",
    sendCode: "Send code via WhatsApp",
    sending: "Sending...",
    codeSentNotice: "We've sent a 6-digit code to your WhatsApp. It's valid for 15 minutes.",
    codeLabel: "Verification code",
    codePlaceholder: "6-digit code",
    verifyCode: "Verify",
    verifying: "Verifying...",
    resend: "Resend code",
    verified: "Phone number verified",
    sendError: "Could not send a code. Please check the number and try again.",
    verifyError: "Could not verify this code. Please try again.",
  },
  emailVerification: {
    emailPlaceholder: "Email (optional)",
    sendLink: "Verify email",
    sending: "Sending...",
    linkSentNotice: "We've sent a verification link to your email. Click it, then come back and confirm below.",
    ivedVerified: "I've verified my email",
    checking: "Checking...",
    verified: "✓ Email verified",
    notYetVerified: "That email isn't verified yet. Check your inbox and click the link first.",
    sendError: "Could not send a verification link. Please check the address and try again.",
  },
};

export default en;
export type Messages = typeof en;
